import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { DRAFT_STATUSES, type DraftStatus } from "@carago/shared";
import { ApprovalsQueueView } from "../components/ApprovalsQueueView.js";
import { ApiClientError, getMe } from "../lib/api.js";
import {
  approveDraft,
  listDrafts,
  patchDraft,
  rejectDraft,
} from "../lib/drafts.js";

const PAGE_SIZE = 20;

function isDraftStatus(value: string): value is DraftStatus {
  return (DRAFT_STATUSES as readonly string[]).includes(value);
}

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusRaw = searchParams.get("status") ?? "pending_approval";
  const statusFilter: DraftStatus | "" =
    statusRaw === "all"
      ? ""
      : isDraftStatus(statusRaw)
        ? statusRaw
        : "pending_approval";

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => getMe(),
  });
  const canApprove =
    meQuery.data?.role === "approver" || meQuery.data?.role === "admin";

  const listQuery = useInfiniteQuery({
    queryKey: ["drafts", statusFilter || "all"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listDrafts({
        status: statusFilter || undefined,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (last) => last.next_cursor,
  });

  const drafts = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (drafts.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !drafts.some((d) => d.id === selectedId)) {
      setSelectedId(drafts[0]!.id);
    }
  }, [drafts, selectedId]);

  const selected = drafts.find((d) => d.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) {
      setEditSubject("");
      setEditBody("");
      return;
    }
    setEditSubject(selected.subject);
    setEditBody(selected.body_text);
    setRejectReason("");
  }, [selected?.id, selected?.subject, selected?.body_text, selected]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["drafts"] });
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      patchDraft(selectedId!, {
        subject: editSubject,
        body_text: editBody,
      }),
    onSuccess: async () => {
      setActionError(null);
      setActionMessage("Entwurf gespeichert.");
      await invalidate();
    },
    onError: (err) => {
      setActionMessage(null);
      setActionError(
        err instanceof ApiClientError
          ? err.message
          : "Speichern fehlgeschlagen.",
      );
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => approveDraft(selectedId!),
    onSuccess: async () => {
      setActionError(null);
      setActionMessage("Freigegeben (nicht gesendet).");
      await invalidate();
    },
    onError: (err) => {
      setActionMessage(null);
      setActionError(
        err instanceof ApiClientError
          ? err.message
          : "Freigabe fehlgeschlagen.",
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      rejectDraft(selectedId!, rejectReason.trim() || undefined),
    onSuccess: async () => {
      setActionError(null);
      setActionMessage("Entwurf abgelehnt.");
      await invalidate();
    },
    onError: (err) => {
      setActionMessage(null);
      setActionError(
        err instanceof ApiClientError
          ? err.message
          : "Ablehnung fehlgeschlagen.",
      );
    },
  });

  const busy =
    saveMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending;

  const state = listQuery.isLoading
    ? "loading"
    : listQuery.isError
      ? "error"
      : drafts.length === 0
        ? "empty"
        : "ready";

  return (
    <ApprovalsQueueView
      state={state}
      errorMessage={
        listQuery.error instanceof ApiClientError
          ? listQuery.error.message
          : listQuery.error
            ? "Entwürfe konnten nicht geladen werden."
            : null
      }
      drafts={drafts}
      statusFilter={statusFilter}
      canApprove={canApprove}
      selectedId={selectedId}
      editSubject={editSubject}
      editBody={editBody}
      rejectReason={rejectReason}
      actionMessage={actionMessage}
      actionError={actionError}
      busy={busy}
      hasNextPage={Boolean(listQuery.hasNextPage)}
      loadingMore={listQuery.isFetchingNextPage}
      onStatusFilterChange={(value) => {
        const next = new URLSearchParams(searchParams);
        if (!value) next.set("status", "all");
        else next.set("status", value);
        setSearchParams(next, { replace: true });
        setActionMessage(null);
        setActionError(null);
      }}
      onSelect={(id) => {
        setSelectedId(id);
        setActionMessage(null);
        setActionError(null);
      }}
      onEditSubjectChange={setEditSubject}
      onEditBodyChange={setEditBody}
      onRejectReasonChange={setRejectReason}
      onSaveEdit={() => {
        if (!selectedId) return;
        saveMutation.mutate();
      }}
      onApprove={() => {
        if (!selectedId) return;
        approveMutation.mutate();
      }}
      onReject={() => {
        if (!selectedId) return;
        rejectMutation.mutate();
      }}
      onLoadMore={() => {
        void listQuery.fetchNextPage();
      }}
    />
  );
}
